import { useMemo, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  NEW_PROGRAM_STATUS,
  blankProgramSeed,
  coerceLocale,
  pickLocalizedText,
  programCloneBody,
  resolveProgramTemplate,
  type EffortScale,
  type Exercise,
  type NewProgram,
  type Program,
  type ProgramModel,
} from "@heraion/shared";

import { difficultyLabel } from "../Library/difficultyLabel";
import { isCoachView } from "../Library/libraryFilters";
import { spokenLabel } from "../Library/LibraryItemCardActions";
import { modelAxes, modelIsRule } from "../Library/modelCatalog";
import { useTemplates } from "../Library/useTemplates";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import { SectionHeader } from "../primitives/SectionHeader";
import { writeError, type ConfirmFn, type DurableWriteFn } from "../seams";
import { resolveTheme, type UiTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { ModelWizard } from "./ModelWizard";
import { makeSetupStyles } from "./setupStyles";
import { StartEntry } from "./StartEntry";

/** «From scratch» (§5.4b): one empty day and four rounds; the name is typed
 *  in the editor. */
const SCRATCH_ROUNDS = 4;

type ListKey = "models" | "templates";
type Stage = { kind: "start" } | { kind: ListKey } | { kind: "wizard"; model: Omit<ProgramModel, "id">; from: ListKey };

type EntryState = { disabled?: boolean; busy?: boolean };

// «New program» (#336 slice 5, #351, brief §5.4b): create your own. From one
// of your models (its wizard), from a public template (the same copy the
// Library makes), or from scratch. Templates and models are rows in the DB,
// read once on mount; an entry with nothing to offer stays, and says why.
// #354: «From a model» lists the caller's own (GET ?scope=mine), with a
// delete each. `initialModel` opens that model's wizard. #472: Metria's
// ready programs are in the Library; a line here leads there. In the coach
// view his Library is under this screen, so the line only leaves. #479:
// Metria's models that are rules (modelIsRule) are listed above his own.
export function NewProgramStart({
  dark,
  apiFetch,
  catalog,
  effortScale,
  confirm,
  durableWrite,
  authoredAs,
  initialModel,
  onOpenLibrary,
  onCreate,
  onCancel,
}: {
  dark: boolean;
  apiFetch: ProgramApiFetch;
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  /** The host's confirm and write; without both, a model of one's own has
   *  no delete. */
  confirm?: ConfirmFn;
  durableWrite?: DurableWriteFn;
  /** The view New program was opened in. */
  authoredAs?: Program["authoredAs"];
  /** A model chosen elsewhere (the Library): start on its wizard. */
  initialModel?: Omit<ProgramModel, "id">;
  /** #472: the host's Library, as the Home opens it. The athlete's line
   *  needs it; absent, no line. */
  onOpenLibrary?: () => void;
  onCreate: (seed: NewProgram) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const local = useMemo(() => makeStyles(theme), [theme]);
  const read = useTemplates({ apiFetch, kinds: ["program", "model", "mine"] });
  const [stage, setStage] = useState<Stage>(
    initialModel ? { kind: "wizard", model: initialModel, from: "models" } : { kind: "start" },
  );
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const toStart = () => setStage({ kind: "start" });
  const mine = read.myModels;
  const ours = useMemo(() => read.models.filter((m) => modelIsRule(modelAxes(m))), [read.models]);
  const toLibrary =
    isCoachView(authoredAs)
      ? onCancel
      : onOpenLibrary
        ? () => {
            onCancel();
            onOpenLibrary();
          }
        : undefined;

  if (stage.kind === "wizard") {
    return (
      <ModelWizard
        theme={theme}
        model={stage.model}
        catalog={catalog}
        effortScale={effortScale}
        onCreate={onCreate}
        onBack={() => setStage({ kind: stage.from })}
      />
    );
  }

  const removeModel = async (m: ProgramModel) => {
    if (!confirm || !durableWrite || deleting) return;
    const name = pickLocalizedText(m.name, lang);
    const ok = await confirm({
      title: t("editorV3.model.deleteTitle", { defaultValue: "Delete this model?" }),
      body: t("editorV3.model.deleteBody", {
        name,
        defaultValue: "\"{{name}}\" goes away. Programs made from it stay.",
      }),
      confirmLabel: t("workout.delete", { defaultValue: "Delete" }),
      cancelLabel: t("program.cancel", { defaultValue: "Cancel" }),
    });
    if (!ok) return;
    setDeleting(m.id);
    setDeleteFailed(false);
    let failed = true;
    try {
      const err = writeError(
        await durableWrite({
          method: "DELETE",
          path: `/api/templates/models/${encodeURIComponent(m.id)}?scope=mine`,
        }),
      );
      if (err) console.warn("[newProgram] delete_model_failed", err.message);
      else failed = false;
    } catch (e) {
      console.warn("[newProgram] delete_model_failed", e instanceof Error ? e.message : e);
    } finally {
      setDeleting(null);
      setDeleteFailed(failed);
    }
    if (!failed) read.reload();
  };

  const titleOf = (key: ListKey) =>
    key === "models"
      ? t("editorV3.model.fromModel", { defaultValue: "From a model" })
      : t("editorV3.model.fromTemplate", { defaultValue: "From a template" });
  const entry = (key: string, title: string, body: string, onPress: () => void, state: EntryState = {}) => (
    <StartEntry
      key={key}
      theme={theme}
      title={title}
      body={body}
      onPress={onPress}
      disabled={state.disabled === true}
      busy={state.busy === true}
    />
  );
  const back = (
    <Button theme={theme} variant="ghost" label={t("editorV3.tetrad.back", { defaultValue: "Back" })} onPress={toStart} full />
  );

  const modelEntry = (m: ProgramModel, from: ListKey) => {
    // Only the row being deleted says so; the others stay as they are.
    const gone = deleting === m.id;
    return entry(
      m.id,
      pickLocalizedText(m.name, lang),
      gone ? t("editorV3.model.deleting", { defaultValue: "Deleting…" }) : pickLocalizedText(m.description, lang),
      () => setStage({ kind: "wizard", model: m, from }),
      gone ? { disabled: true, busy: true } : {},
    );
  };

  if (stage.kind === "models" || stage.kind === "templates") {
    return (
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title} accessibilityRole="header" role="heading">
          {titleOf(stage.kind)}
        </Text>
        {stage.kind === "models" ? (
          <>
            {ours.length > 0 ? (
              <>
                <SectionHeader theme={theme} title={t("editorV3.model.ours", { defaultValue: "$t(brand.product) models" })} />
                {ours.map((m) => modelEntry(m, "models"))}
              </>
            ) : null}
            <SectionHeader theme={theme} title={t("editorV3.model.mine", { defaultValue: "My models" })} />
            {deleteFailed ? (
              <Text style={local.error} accessibilityLiveRegion="polite" aria-live="polite">
                {t("editorV3.model.deleteError", { defaultValue: "Could not delete the model. Try again." })}
              </Text>
            ) : null}
            {mine.length === 0 ? (
              <Text style={styles.hint}>
                {t("editorV3.model.noMine", {
                  defaultValue: "None yet. In the Library, tap \"Save as model\" on one of your programs.",
                })}
              </Text>
            ) : null}
            {mine.map((m) => (
              <View key={m.id} style={local.mineRow}>
                <View style={local.mineEntry}>{modelEntry(m, "models")}</View>
                {confirm && durableWrite ? (
                  <IconButton
                    theme={theme}
                    name="trash"
                    variant="destructive"
                    onPress={() => void removeModel(m)}
                    disabled={deleting === m.id}
                    accessibilityLabel={spokenLabel(
                      t("workout.delete", { defaultValue: "Delete" }),
                      pickLocalizedText(m.name, lang),
                    )}
                  />
                ) : null}
              </View>
            ))}
          </>
        ) : (
          read.programTemplates.map((tpl) => {
            const resolved = resolveProgramTemplate(tpl, lang);
            return entry(
              tpl.id,
              resolved.name,
              `${difficultyLabel(tpl.difficulty, t)} · ${resolved.description}`,
              // The Library's copy, which the editor opens.
              () => onCreate(programCloneBody(resolved, { status: NEW_PROGRAM_STATUS })),
            );
          })
        )}
        {back}
      </ScrollView>
    );
  }

  // One read for all lists: loading, error and empty speak in each entry.
  const listEntry = (key: ListKey, count: number, body: string, none: string) =>
    read.status === "loading"
      ? entry(key, titleOf(key), t("editorV3.model.loading", { defaultValue: "Loading…" }), () => {}, {
          disabled: true,
          busy: true,
        })
      : read.status === "error"
        ? entry(key, titleOf(key), t("editorV3.model.loadError", { defaultValue: "Could not load. Tap to try again." }), read.reload)
        : count === 0
          ? entry(key, titleOf(key), none, () => {}, { disabled: true })
          : entry(key, titleOf(key), body, () => setStage({ kind: key }));

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Text style={styles.title} accessibilityRole="header" role="heading">
        {t("program.newTitle", { defaultValue: "New program" })}
      </Text>
      {toLibrary ? (
        <View style={local.ready}>
          <Text style={styles.hint}>
            {t("editorV3.model.readyAsk", { defaultValue: "Want a ready program?" })}
          </Text>
          <Button
            theme={theme}
            variant="ghost"
            label={t("library.catalog.title", { defaultValue: "$t(brand.product) programs" })}
            onPress={toLibrary}
          />
        </View>
      ) : null}
      {listEntry(
        "models",
        ours.length + mine.length,
        t("editorV3.model.fromModelBody", {
          defaultValue: "The model writes the days and the rounds. You choose the level, the time per session and the exercises.",
        }),
        // The way to a model of one's own, said where it is read (#479).
        t("editorV3.model.noMine", {
          defaultValue: "None yet. In the Library, tap \"Save as model\" on one of your programs.",
        }),
      )}
      {listEntry(
        "templates",
        read.programTemplates.length,
        t("editorV3.model.fromTemplateBody", { defaultValue: "A copy of a public program, to change as you like." }),
        t("editorV3.model.noTemplate", { defaultValue: "No template yet." }),
      )}
      {entry(
        "scratch",
        t("editorV3.tetrad.scratchTitle", { defaultValue: "From scratch" }),
        t("editorV3.tetrad.scratchBody", { defaultValue: "One empty day and four rounds." }),
        () => onCreate(blankProgramSeed("", SCRATCH_ROUNDS, 1)),
      )}
      <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={onCancel} full />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // A model of one's own: its entry, and the delete beside it.
    mineRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    mineEntry: { flex: 1 },
    // #472: the way to the ready programs, one line under the title.
    ready: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: s.s2 },
    error: { color: p.danger, fontSize: ty.sizes.sm },
  });
}
